import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useApi } from "./ApiContext";

const PropertiesCtx = createContext(null);

export function PropertiesProvider({ children }) {
  const { fetchProperties } = useApi();

  // filters
  const [selectedType, setSelectedType] = useState("All");          // Rent / Sale / All
  const [selectedCategory, setSelectedCategory] = useState("Residential");
  const [selectedLocation, setSelectedLocation] = useState("All");

  // data
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // load from backend when location changes
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError("");
    fetchProperties({ location: selectedLocation !== "All" ? selectedLocation : undefined })
      .then((data) => { if (isMounted) setItems(Array.isArray(data) ? data : []); })
      .catch((e) => { if (isMounted) setError(e.message || "Failed to load"); })
      .finally(() => { if (isMounted) setLoading(false); });
    return () => { isMounted = false; };
  }, [fetchProperties, selectedLocation]);

  // unique locations from fetched list
  const locations = useMemo(() => {
    const set = new Set(items.map(p => p.location).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items]);

  // client-side filtering for category + type
  const filtered = useMemo(() => {
    return items.filter((p) => {
      const matchesType = selectedType === "All" || p.status === selectedType;
      const matchesCategory = !selectedCategory || p.category === selectedCategory;
      return matchesType && matchesCategory;
    });
  }, [items, selectedType, selectedCategory]);

  const value = {
    items,
    filtered,
    loading,
    error,
    // filters
    selectedType, setSelectedType,
    selectedCategory, setSelectedCategory,
    selectedLocation, setSelectedLocation,
    locations,
  };

  return <PropertiesCtx.Provider value={value}>{children}</PropertiesCtx.Provider>;
}

export function useProperties() {
  const ctx = useContext(PropertiesCtx);
  if (!ctx) throw new Error("useProperties must be used within <PropertiesProvider>");
  return ctx;
}
